import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Users, Search, Trash2, Edit, Plus, Download, Upload, Loader2, ArrowLeft, ShieldX, Star, Briefcase, Zap, Globe, Link, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL Import Dialog Component
function ImportFromURLDialog({ open, onClose, onImport, importing, title }) {
  const [url, setUrl] = useState("");
  
  const handleImport = () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    onImport(url.trim());
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Import {title} from Website
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">
            Paste the website URL and we'll automatically extract all relevant information including team members, portfolio, focus areas, and more.
          </p>
          <div className="space-y-2">
            <Label>Website URL</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={importing || !url.trim()}>
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" />
                Extract Data
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminInvestors() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("firms");
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Firms state
  const [firms, setFirms] = useState([]);
  const [firmsTotal, setFirmsTotal] = useState(0);
  const [firmsSearch, setFirmsSearch] = useState("");
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsSearch, setContactsSearch] = useState("");
  const [selectedFirmFilter, setSelectedFirmFilter] = useState("");
  
  // Dialogs
  const [firmDialog, setFirmDialog] = useState({ open: false, firm: null });
  const [contactDialog, setContactDialog] = useState({ open: false, contact: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [urlImportDialog, setUrlImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);
  
  // Form state
  const [firmForm, setFirmForm] = useState({
    name: "", investment_thesis: "", stage_focus: "", leads_rounds: false,
    min_ticket_size: "", max_ticket_size: "", portfolio_companies: "",
    focus_areas: "", is_angel: false, location: "", website: "",
    portfolio_current: [], portfolio_exited: [],
    eis_seis_only: false, investment_scheme: "",
    trusted_network: []  // Trusted contacts for access path
  });
  
  // New trusted contact form state
  const [trustedContactForm, setTrustedContactForm] = useState({
    name: "", comment: "", company: "", role: "", linkedin_url: "", relationship_type: ""
  });
  
  const [editTab, setEditTab] = useState("basic");
  
  const [contactForm, setContactForm] = useState({
    firm_id: "", first_name: "", last_name: "", role: "", email: "",
    linkedin_url: "", board_seats: "", intel: "", is_primary_contact: false
  });

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchFirms();
      fetchContacts();
      fetchRefreshStatus();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      const t = setTimeout(fetchFirms, 300);
      return () => clearTimeout(t);
    }
  }, [firmsSearch, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      const t = setTimeout(fetchContacts, 300);
      return () => clearTimeout(t);
    }
  }, [contactsSearch, selectedFirmFilter, isAdmin]);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) { navigate("/auth"); return; }
      setUser(await userRes.json());

      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      if (adminRes.ok) {
        const data = await adminRes.json();
        setIsAdmin(data.is_admin);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      navigate("/auth");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/investor-stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchFirms = async () => {
    try {
      const params = new URLSearchParams();
      if (firmsSearch) params.append("search", firmsSearch);
      const res = await fetch(`${BACKEND_URL}/api/admin/firms?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setFirms(data.firms);
        setFirmsTotal(data.total);
      }
    } catch (e) { console.error(e); }
  };

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (contactsSearch) params.append("search", contactsSearch);
      if (selectedFirmFilter) params.append("firm_id", selectedFirmFilter);
      const res = await fetch(`${BACKEND_URL}/api/admin/contacts?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts);
        setContactsTotal(data.total);
      }
    } catch (e) { console.error(e); }
  };

  const handleExportFirms = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/firms/export/csv`, { credentials: "include" });
    if (res.ok) {
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'vc_firms.csv';
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Exported");
    }
  };

  const fetchRefreshStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/firms/refresh-status`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRefreshStatus(data);
      }
    } catch (e) { console.error(e); }
  };

  const handleRefreshAllFirms = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/firms/refresh-all`, {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Portfolio refresh started! This runs in the background and may take several minutes.");
        // Poll for status updates
        setTimeout(fetchRefreshStatus, 30000);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to start refresh");
      }
    } catch (e) {
      toast.error("Failed to start refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const handleExportContacts = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/contacts/export/csv`, { credentials: "include" });
    if (res.ok) {
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'contacts.csv';
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Exported");
    }
  };

  const handleUploadFirms = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${BACKEND_URL}/api/admin/firms/bulk-upload`, {
      method: "POST", credentials: "include", body: formData
    });
    const data = await res.json();
    if (res.ok) {
      toast.success(`Added ${data.firms_added}, updated ${data.firms_updated}`);
      fetchFirms(); fetchStats();
    } else {
      toast.error(data.detail || "Upload failed");
    }
    e.target.value = '';
  };

  const handleUploadContacts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${BACKEND_URL}/api/admin/contacts/bulk-upload`, {
      method: "POST", credentials: "include", body: formData
    });
    const data = await res.json();
    if (res.ok) {
      toast.success(`Added ${data.contacts_added}, updated ${data.contacts_updated}`);
      fetchContacts(); fetchStats();
    } else {
      toast.error(data.detail || "Upload failed");
    }
    e.target.value = '';
  };

  const handleImportFromURL = async (url) => {
    if (!url || !url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    setImporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/investors/import-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const data = await response.json();
        const extracted = data.extracted_data;
        
        // Convert extracted portfolio data to arrays
        const newCurrentArr = Object.entries(extracted.portfolio_current || {}).map(([name, details]) => ({
          name, indication: details?.indication || "", modality: details?.modality || "", stage: details?.stage || ""
        }));
        const newExitedArr = Object.entries(extracted.portfolio_exited || {}).map(([name, details]) => ({
          name, indication: details?.indication || "", modality: details?.modality || "", outcome: details?.outcome || ""
        }));
        const newContactsArr = (extracted.contacts || []).map(c => ({
          name: c.name || "", role: c.role || "", email: c.email || "", linkedin: c.linkedin || ""
        }));
        
        // Check if we're editing an existing firm (merge mode) or creating new (replace mode)
        const isEditing = firmDialog.firm !== null;
        
        if (isEditing) {
          // MERGE MODE: Fill empty fields, append new portfolio/contacts
          const existingPortfolioNames = new Set(
            firmForm.portfolio_current.filter(p => p.name).map(p => p.name.toLowerCase())
          );
          const existingExitedNames = new Set(
            firmForm.portfolio_exited.filter(p => p.name).map(p => p.name.toLowerCase())
          );
          const existingContactNames = new Set(
            firmForm.contacts.filter(c => c.name).map(c => c.name.toLowerCase())
          );
          
          // Filter out duplicates from new data
          const uniqueNewCurrent = newCurrentArr.filter(p => p.name && !existingPortfolioNames.has(p.name.toLowerCase()));
          const uniqueNewExited = newExitedArr.filter(p => p.name && !existingExitedNames.has(p.name.toLowerCase()));
          const uniqueNewContacts = newContactsArr.filter(c => c.name && !existingContactNames.has(c.name.toLowerCase()));
          
          // Merge: existing data + new unique data
          const mergedCurrent = [
            ...firmForm.portfolio_current.filter(p => p.name), // Keep existing non-empty
            ...uniqueNewCurrent
          ];
          const mergedExited = [
            ...firmForm.portfolio_exited.filter(p => p.name),
            ...uniqueNewExited
          ];
          const mergedContacts = [
            ...firmForm.contacts.filter(c => c.name),
            ...uniqueNewContacts
          ];
          
          // Add empty row if arrays are empty
          if (mergedCurrent.length === 0) mergedCurrent.push({ name: "", indication: "", modality: "", stage: "" });
          if (mergedExited.length === 0) mergedExited.push({ name: "", indication: "", modality: "", outcome: "" });
          if (mergedContacts.length === 0) mergedContacts.push({ name: "", role: "", email: "", linkedin: "" });
          
          setFirmForm(prev => ({
            // Only fill empty fields, keep existing values
            name: prev.name || extracted.name || "",
            investment_thesis: prev.investment_thesis || extracted.investment_thesis || "",
            stage_focus: prev.stage_focus || (extracted.stage_focus || []).join(", "),
            leads_rounds: prev.leads_rounds || extracted.leads_rounds || false,
            min_ticket_size: prev.min_ticket_size || extracted.min_ticket_size || "",
            max_ticket_size: prev.max_ticket_size || extracted.max_ticket_size || "",
            portfolio_companies: prev.portfolio_companies || "",
            focus_areas: prev.focus_areas || (extracted.focus_areas || []).join(", "),
            is_angel: prev.is_angel,
            location: prev.location || extracted.location || "",
            website: prev.website || extracted.website || url,
            // Append new items to lists
            portfolio_current: mergedCurrent,
            portfolio_exited: mergedExited,
            contacts: mergedContacts
          }));
          
          const addedCount = uniqueNewCurrent.length + uniqueNewExited.length + uniqueNewContacts.length;
          toast.success(`Data merged! Added ${uniqueNewCurrent.length} portfolio companies, ${uniqueNewExited.length} exits, ${uniqueNewContacts.length} contacts.`);
        } else {
          // NEW MODE: Replace all fields with extracted data
          setFirmForm({
            name: extracted.name || "",
            investment_thesis: extracted.investment_thesis || "",
            stage_focus: (extracted.stage_focus || []).join(", "),
            leads_rounds: extracted.leads_rounds || false,
            min_ticket_size: extracted.min_ticket_size || "",
            max_ticket_size: extracted.max_ticket_size || "",
            portfolio_companies: "",
            focus_areas: (extracted.focus_areas || []).join(", "),
            is_angel: false,
            location: extracted.location || "",
            website: extracted.website || url,
            portfolio_current: newCurrentArr.length > 0 ? newCurrentArr : [{ name: "", indication: "", modality: "", stage: "" }],
            portfolio_exited: newExitedArr.length > 0 ? newExitedArr : [{ name: "", indication: "", modality: "", outcome: "" }],
            contacts: newContactsArr.length > 0 ? newContactsArr : [{ name: "", role: "", email: "", linkedin: "" }]
          });
          toast.success("Data extracted! Review the form below.");
        }
        
        setImportUrl("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to extract data");
      }
    } catch (error) {
      toast.error("Failed to extract data from URL");
    } finally {
      setImporting(false);
    }
  };

  const openFirmDialog = (firm = null) => {
    setEditTab("basic");
    if (firm) {
      // Convert portfolio objects to arrays for editing
      const currentArr = Object.entries(firm.portfolio_current || {}).map(([name, details]) => ({
        name, indication: details.indication || "", modality: details.modality || "", stage: details.stage || ""
      }));
      const exitedArr = Object.entries(firm.portfolio_exited || {}).map(([name, details]) => ({
        name, indication: details.indication || "", modality: details.modality || "", outcome: details.outcome || ""
      }));
      const contactsArr = (firm.contacts || []).map(c => ({
        name: c.name || "", role: c.role || "", email: c.email || "", linkedin: c.linkedin || ""
      }));
      
      setFirmForm({
        name: firm.name || "",
        investment_thesis: firm.investment_thesis || "",
        stage_focus: (firm.stage_focus || []).join(", "),
        leads_rounds: firm.leads_rounds || false,
        eis_seis_only: firm.eis_seis_only || false,
        investment_scheme: firm.investment_scheme || "",
        min_ticket_size: firm.min_ticket_size || "",
        max_ticket_size: firm.max_ticket_size || "",
        portfolio_companies: (firm.portfolio_companies || []).join(", "),
        focus_areas: (firm.focus_areas || []).join(", "),
        is_angel: firm.is_angel || false,
        location: firm.location || "",
        website: firm.website || "",
        portfolio_current: currentArr.length > 0 ? currentArr : [{ name: "", indication: "", modality: "", stage: "" }],
        portfolio_exited: exitedArr.length > 0 ? exitedArr : [{ name: "", indication: "", modality: "", outcome: "" }],
        contacts: contactsArr.length > 0 ? contactsArr : [{ name: "", role: "", email: "", linkedin: "" }],
        trusted_network: firm.trusted_network || []
      });
    } else {
      setFirmForm({
        name: "", investment_thesis: "", stage_focus: "", leads_rounds: false,
        eis_seis_only: false, investment_scheme: "",
        min_ticket_size: "", max_ticket_size: "", portfolio_companies: "",
        focus_areas: "", is_angel: false, location: "", website: "",
        portfolio_current: [{ name: "", indication: "", modality: "", stage: "" }],
        portfolio_exited: [{ name: "", indication: "", modality: "", outcome: "" }],
        contacts: [{ name: "", role: "", email: "", linkedin: "" }],
        trusted_network: []
      });
    }
    setFirmDialog({ open: true, firm });
  };

  const handleSaveFirm = async () => {
    if (!firmForm.name) { toast.error("Name required"); return; }
    setSaving(true);
    
    // Convert portfolio arrays back to objects
    const portfolioCurrent = {};
    firmForm.portfolio_current.forEach(item => {
      if (item.name) {
        portfolioCurrent[item.name] = {
          indication: item.indication,
          modality: item.modality,
          stage: item.stage
        };
      }
    });
    
    const portfolioExited = {};
    firmForm.portfolio_exited.forEach(item => {
      if (item.name) {
        portfolioExited[item.name] = {
          indication: item.indication,
          modality: item.modality,
          outcome: item.outcome
        };
      }
    });
    
    // Filter out empty contacts
    const contacts = (firmForm.contacts || []).filter(c => c.name).map(c => ({
      name: c.name,
      role: c.role || "",
      email: c.email || "",
      linkedin: c.linkedin || ""
    }));
    
    const payload = {
      name: firmForm.name,
      investment_thesis: firmForm.investment_thesis || null,
      stage_focus: typeof firmForm.stage_focus === 'string' ? firmForm.stage_focus.split(",").map(s => s.trim()).filter(Boolean) : (firmForm.stage_focus || []),
      leads_rounds: firmForm.leads_rounds,
      eis_seis_only: firmForm.eis_seis_only,
      investment_scheme: firmForm.investment_scheme || null,
      min_ticket_size: firmForm.min_ticket_size || null,
      max_ticket_size: firmForm.max_ticket_size || null,
      portfolio_companies: typeof firmForm.portfolio_companies === 'string' ? firmForm.portfolio_companies.split(",").map(s => s.trim()).filter(Boolean) : (firmForm.portfolio_companies || []),
      focus_areas: typeof firmForm.focus_areas === 'string' ? firmForm.focus_areas.split(",").map(s => s.trim()).filter(Boolean) : (firmForm.focus_areas || []),
      is_angel: firmForm.is_angel,
      location: firmForm.location || null,
      website: firmForm.website || null,
      portfolio_current: portfolioCurrent,
      portfolio_exited: portfolioExited,
      contacts: contacts,
      trusted_network: firmForm.trusted_network || []
    };
    const url = firmDialog.firm 
      ? `${BACKEND_URL}/api/admin/firms/${firmDialog.firm.firm_id}`
      : `${BACKEND_URL}/api/admin/firms`;
    const res = await fetch(url, {
      method: firmDialog.firm ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      toast.success(firmDialog.firm ? "Updated" : "Added");
      fetchFirms(); fetchStats();
      setFirmDialog({ open: false, firm: null });
    } else {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const openContactDialog = (contact = null) => {
    if (contact) {
      setContactForm({
        firm_id: contact.firm_id || "",
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        role: contact.role || "",
        email: contact.email || "",
        linkedin_url: contact.linkedin_url || "",
        board_seats: (contact.board_seats || []).join(", "),
        intel: contact.intel || "",
        is_primary_contact: contact.is_primary_contact || false
      });
    } else {
      setContactForm({
        firm_id: "", first_name: "", last_name: "", role: "", email: "",
        linkedin_url: "", board_seats: "", intel: "", is_primary_contact: false
      });
    }
    setContactDialog({ open: true, contact });
  };

  const handleSaveContact = async () => {
    if (!contactForm.first_name || !contactForm.last_name) { 
      toast.error("First and last name required"); return; 
    }
    setSaving(true);
    const payload = {
      firm_id: contactForm.firm_id || null,
      first_name: contactForm.first_name,
      last_name: contactForm.last_name,
      role: contactForm.role || null,
      email: contactForm.email || null,
      linkedin_url: contactForm.linkedin_url || null,
      board_seats: contactForm.board_seats.split(",").map(s => s.trim()).filter(Boolean),
      intel: contactForm.intel || null,
      is_primary_contact: contactForm.is_primary_contact
    };
    const url = contactDialog.contact 
      ? `${BACKEND_URL}/api/admin/contacts/${contactDialog.contact.contact_id}`
      : `${BACKEND_URL}/api/admin/contacts`;
    const res = await fetch(url, {
      method: contactDialog.contact ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      toast.success(contactDialog.contact ? "Updated" : "Added");
      fetchContacts(); fetchStats();
      setContactDialog({ open: false, contact: null });
    } else {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    setSaving(true);
    const url = deleteDialog.type === "firm"
      ? `${BACKEND_URL}/api/admin/firms/${deleteDialog.item.firm_id}`
      : `${BACKEND_URL}/api/admin/contacts/${deleteDialog.item.contact_id}`;
    const res = await fetch(url, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("Deleted");
      if (deleteDialog.type === "firm") fetchFirms();
      else fetchContacts();
      fetchStats();
      setDeleteDialog({ open: false, type: null, item: null });
    } else {
      toast.error("Failed");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 py-16">
          <Card className="max-w-md mx-auto border border-red-200">
            <CardContent className="p-8 text-center">
              <ShieldX className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-slate-500 mb-6">You don't have permission.</p>
              <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-investors-page">
      <Navbar user={user} />
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Investor Management</h1>
            <p className="text-slate-600">Manage VC firms and contacts</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-teal-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_firms}</p>
                  <p className="text-xs text-slate-500">Total Firms</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.vc_firms}</p>
                  <p className="text-xs text-slate-500">VC Firms</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.angel_investors}</p>
                  <p className="text-xs text-slate-500">Angels</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_contacts}</p>
                  <p className="text-xs text-slate-500">Contacts</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Buttons */}
        <div className="flex gap-2 mb-4">
          <Button 
            variant={activeTab === "firms" ? "default" : "outline"}
            onClick={() => setActiveTab("firms")}
            className="gap-2"
          >
            <Building2 className="w-4 h-4" />
            VC Firms ({firmsTotal})
          </Button>
          <Button 
            variant={activeTab === "contacts" ? "default" : "outline"}
            onClick={() => setActiveTab("contacts")}
            className="gap-2"
          >
            <Users className="w-4 h-4" />
            Contacts ({contactsTotal})
          </Button>
        </div>

        {/* Firms Tab */}
        {activeTab === "firms" && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search firms..." 
                    value={firmsSearch} 
                    onChange={(e) => setFirmsSearch(e.target.value)} 
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    onClick={handleRefreshAllFirms} 
                    disabled={refreshing}
                    size="sm"
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    title={refreshStatus?.last_sync ? `Last refresh: ${new Date(refreshStatus.last_sync).toLocaleDateString()}` : "Refresh all firm portfolios from websites"}
                  >
                    {refreshing ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    {refreshing ? "Refreshing..." : "Refresh All"}
                  </Button>
                  <Button variant="outline" onClick={handleExportFirms} size="sm">
                    <Download className="w-4 h-4 mr-1" />Export
                  </Button>
                  <label className="cursor-pointer">
                    <Button variant="outline" size="sm" asChild>
                      <span><Upload className="w-4 h-4 mr-1" />Import CSV</span>
                    </Button>
                    <input type="file" accept=".csv" onChange={handleUploadFirms} className="hidden" />
                  </label>
                  <Button onClick={() => openFirmDialog()} size="sm" data-testid="add-firm-button">
                    <Plus className="w-4 h-4 mr-1" />Add Firm
                  </Button>
                </div>
              </div>
              {/* Last refresh status */}
              {refreshStatus?.last_sync && (
                <div className="text-xs text-slate-500 mt-2">
                  Last auto-refresh: {new Date(refreshStatus.last_sync).toLocaleDateString()} 
                  {refreshStatus.firms_refreshed > 0 && ` (${refreshStatus.firms_refreshed} firms updated)`}
                  {" • "}Next: 1st of month
                </div>
              )}
            </CardHeader>
            <CardContent>
              {firms.length === 0 ? (
                <p className="text-center py-8 text-slate-500">No firms found</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Firm</TableHead>
                        <TableHead>Focus</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Ticket</TableHead>
                        <TableHead>Contacts</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {firms.map((firm) => (
                        <TableRow key={firm.firm_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{firm.name}</p>
                              {firm.is_angel && <Badge variant="secondary">Angel</Badge>}
                              {firm.location && <p className="text-xs text-slate-500">{firm.location}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {firm.focus_areas?.slice(0,2).map((a,i) => (
                                <Badge key={i} variant="outline" className="text-xs">{a}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {firm.stage_focus?.slice(0,2).join(", ")}
                          </TableCell>
                          <TableCell className="text-sm">
                            {firm.min_ticket_size && firm.max_ticket_size 
                              ? `${firm.min_ticket_size}-${firm.max_ticket_size}` 
                              : firm.min_ticket_size || firm.max_ticket_size || "—"}
                          </TableCell>
                          <TableCell>{firm.contact_count || 0}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openFirmDialog(firm)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, type: "firm", item: firm })}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search contacts..." 
                    value={contactsSearch} 
                    onChange={(e) => setContactsSearch(e.target.value)} 
                    className="pl-10"
                  />
                </div>
                <Select value={selectedFirmFilter || "all"} onValueChange={(v) => setSelectedFirmFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by Firm" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Firms</SelectItem>
                    {firms.map(f => (
                      <SelectItem key={f.firm_id} value={f.firm_id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={handleExportContacts} size="sm">
                    <Download className="w-4 h-4 mr-1" />Export
                  </Button>
                  <label className="cursor-pointer">
                    <Button variant="outline" size="sm" asChild>
                      <span><Upload className="w-4 h-4 mr-1" />Import</span>
                    </Button>
                    <input type="file" accept=".csv" onChange={handleUploadContacts} className="hidden" />
                  </label>
                  <Button onClick={() => openContactDialog()} size="sm">
                    <Plus className="w-4 h-4 mr-1" />Add Contact
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-center py-8 text-slate-500">No contacts found</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Firm</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Primary</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.map((contact) => (
                        <TableRow key={contact.contact_id}>
                          <TableCell className="font-medium">
                            {contact.first_name} {contact.last_name}
                          </TableCell>
                          <TableCell>{contact.firm_name || "Independent"}</TableCell>
                          <TableCell>{contact.role || "—"}</TableCell>
                          <TableCell className="text-sm">{contact.email || "—"}</TableCell>
                          <TableCell>
                            {contact.is_primary_contact && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openContactDialog(contact)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, type: "contact", item: contact })}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Firm Dialog */}
      <Dialog open={firmDialog.open} onOpenChange={(open) => setFirmDialog({ open, firm: firmDialog.firm })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{firmDialog.firm ? "Edit Firm" : "Add Firm"}</DialogTitle>
          </DialogHeader>
          
          {/* URL Import Section - shown for both new and editing */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="url-import-section">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {firmDialog.firm ? "Complete from Website" : "Import from Website"}
              </span>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://vc-firm-website.com"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImportFromURL(importUrl)}
                  data-testid="url-import-input"
                />
              </div>
              <Button 
                onClick={() => handleImportFromURL(importUrl)} 
                disabled={importing || !importUrl.trim()}
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
                data-testid="url-extract-button"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 animate-spin" data-testid="extracting-spinner" />
                ) : (
                  <Globe className="w-4 h-4" />
                )}
                <span className="ml-2">{importing ? "Extracting..." : "Extract"}</span>
              </Button>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              {firmDialog.firm 
                ? "Fill empty fields and add new portfolio companies & partners (won't overwrite existing data)"
                : "Paste a VC firm URL to auto-fill portfolio, team, investment thesis, and more"
              }
            </p>
          </div>
          
          <Tabs value={editTab} onValueChange={setEditTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="basic" data-testid="basic-tab-trigger">Basic Info</TabsTrigger>
              <TabsTrigger value="partners" data-testid="partners-tab-trigger">Partners</TabsTrigger>
              <TabsTrigger value="current" data-testid="portfolio-tab-trigger">Portfolio</TabsTrigger>
              <TabsTrigger value="exits" data-testid="exits-tab-trigger">Exits</TabsTrigger>
              <TabsTrigger value="network" data-testid="network-tab-trigger">Trusted Network</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" data-testid="basic-info-tab">
              <div className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={firmForm.name} onChange={(e) => setFirmForm({...firmForm, name: e.target.value})} data-testid="firm-name-input" />
                </div>
                <div>
                  <Label>Investment Thesis</Label>
                  <Textarea value={firmForm.investment_thesis} onChange={(e) => setFirmForm({...firmForm, investment_thesis: e.target.value})} rows={2} data-testid="investment-thesis-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Ticket</Label>
                    <Input value={firmForm.min_ticket_size} onChange={(e) => setFirmForm({...firmForm, min_ticket_size: e.target.value})} placeholder="$500K" data-testid="min-ticket-input" />
                  </div>
                  <div>
                    <Label>Max Ticket</Label>
                    <Input value={firmForm.max_ticket_size} onChange={(e) => setFirmForm({...firmForm, max_ticket_size: e.target.value})} placeholder="$10M" data-testid="max-ticket-input" />
                  </div>
                </div>
                <div>
                  <Label>Stage Focus (comma-separated)</Label>
                  <Input value={firmForm.stage_focus} onChange={(e) => setFirmForm({...firmForm, stage_focus: e.target.value})} placeholder="Seed, Series A" data-testid="stage-focus-input" />
                </div>
                <div>
                  <Label>Focus Areas (comma-separated)</Label>
                  <Input value={firmForm.focus_areas} onChange={(e) => setFirmForm({...firmForm, focus_areas: e.target.value})} placeholder="Therapeutics, Oncology" data-testid="focus-areas-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Location</Label>
                    <Input value={firmForm.location} onChange={(e) => setFirmForm({...firmForm, location: e.target.value})} data-testid="location-input" />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input value={firmForm.website} onChange={(e) => setFirmForm({...firmForm, website: e.target.value})} data-testid="website-input" />
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={firmForm.leads_rounds} onChange={(e) => setFirmForm({...firmForm, leads_rounds: e.target.checked})} data-testid="leads-rounds-checkbox" />
                    Leads Rounds
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={firmForm.is_angel} onChange={(e) => setFirmForm({...firmForm, is_angel: e.target.checked})} data-testid="is-angel-checkbox" />
                    Angel Investor
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={firmForm.eis_seis_only} onChange={(e) => setFirmForm({...firmForm, eis_seis_only: e.target.checked})} data-testid="eis-seis-checkbox" />
                    EIS/SEIS Only
                  </label>
                </div>
                <div>
                  <Label>Investment Scheme (if applicable)</Label>
                  <Input 
                    value={firmForm.investment_scheme} 
                    onChange={(e) => setFirmForm({...firmForm, investment_scheme: e.target.value})} 
                    placeholder="e.g., EIS, SEIS, VCT, etc."
                    data-testid="investment-scheme-input" 
                  />
                </div>
              </div>
            </TabsContent>
            
            {/* Partners Tab */}
            <TabsContent value="partners" data-testid="partners-tab-content">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> Key Partners & Investors
                  </Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFirmForm({
                      ...firmForm, 
                      contacts: [...(firmForm.contacts || []), { name: "", role: "", email: "", linkedin: "" }]
                    })}
                    data-testid="add-partner-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Partner
                  </Button>
                </div>
                {(firmForm.contacts || []).map((contact, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-2 bg-slate-50" data-testid={`partner-row-${idx}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-600">Partner {idx + 1}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const updated = firmForm.contacts.filter((_, i) => i !== idx);
                          setFirmForm({ ...firmForm, contacts: updated.length > 0 ? updated : [{ name: "", role: "", email: "", linkedin: "" }] });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input 
                        placeholder="Name" 
                        value={contact.name}
                        onChange={(e) => {
                          const updated = [...firmForm.contacts];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setFirmForm({ ...firmForm, contacts: updated });
                        }}
                        data-testid={`partner-name-${idx}`}
                      />
                      <Input 
                        placeholder="Role (e.g., Partner, Principal)" 
                        value={contact.role}
                        onChange={(e) => {
                          const updated = [...firmForm.contacts];
                          updated[idx] = { ...updated[idx], role: e.target.value };
                          setFirmForm({ ...firmForm, contacts: updated });
                        }}
                        data-testid={`partner-role-${idx}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input 
                        placeholder="Email" 
                        value={contact.email}
                        onChange={(e) => {
                          const updated = [...firmForm.contacts];
                          updated[idx] = { ...updated[idx], email: e.target.value };
                          setFirmForm({ ...firmForm, contacts: updated });
                        }}
                      />
                      <Input 
                        placeholder="LinkedIn URL" 
                        value={contact.linkedin}
                        onChange={(e) => {
                          const updated = [...firmForm.contacts];
                          updated[idx] = { ...updated[idx], linkedin: e.target.value };
                          setFirmForm({ ...firmForm, contacts: updated });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="current" data-testid="portfolio-tab-content">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Current Investments
                  </Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFirmForm({
                      ...firmForm, 
                      portfolio_current: [...firmForm.portfolio_current, { name: "", indication: "", modality: "", stage: "" }]
                    })}
                    data-testid="add-portfolio-company-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                
                {firmForm.portfolio_current.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No current investments. Click Add to add one.</p>
                ) : (
                  firmForm.portfolio_current.map((item, idx) => (
                    <div key={idx} className="p-3 border rounded-lg space-y-2 bg-slate-50" data-testid={`portfolio-company-${idx}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Company {idx + 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            const updated = [...firmForm.portfolio_current];
                            updated.splice(idx, 1);
                            setFirmForm({...firmForm, portfolio_current: updated});
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input 
                        placeholder="Company name"
                        value={item.name}
                        onChange={(e) => {
                          const updated = [...firmForm.portfolio_current];
                          updated[idx].name = e.target.value;
                          setFirmForm({...firmForm, portfolio_current: updated});
                        }}
                        data-testid={`portfolio-company-name-${idx}`}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input 
                          placeholder="Indication"
                          value={item.indication}
                          onChange={(e) => {
                            const updated = [...firmForm.portfolio_current];
                            updated[idx].indication = e.target.value;
                            setFirmForm({...firmForm, portfolio_current: updated});
                          }}
                        />
                        <Input 
                          placeholder="Modality"
                          value={item.modality}
                          onChange={(e) => {
                            const updated = [...firmForm.portfolio_current];
                            updated[idx].modality = e.target.value;
                            setFirmForm({...firmForm, portfolio_current: updated});
                          }}
                        />
                        <Input 
                          placeholder="Stage (e.g. Clinical)"
                          value={item.stage}
                          onChange={(e) => {
                            const updated = [...firmForm.portfolio_current];
                            updated[idx].stage = e.target.value;
                            setFirmForm({...firmForm, portfolio_current: updated});
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="exits">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Exits (IPO / Acquired)
                  </Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFirmForm({
                      ...firmForm, 
                      portfolio_exited: [...firmForm.portfolio_exited, { name: "", indication: "", modality: "", outcome: "" }]
                    })}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                
                {firmForm.portfolio_exited.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No exits recorded. Click Add to add one.</p>
                ) : (
                  firmForm.portfolio_exited.map((item, idx) => (
                    <div key={idx} className="p-3 border rounded-lg space-y-2 bg-green-50">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Exit {idx + 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            const updated = [...firmForm.portfolio_exited];
                            updated.splice(idx, 1);
                            setFirmForm({...firmForm, portfolio_exited: updated});
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input 
                        placeholder="Company name"
                        value={item.name}
                        onChange={(e) => {
                          const updated = [...firmForm.portfolio_exited];
                          updated[idx].name = e.target.value;
                          setFirmForm({...firmForm, portfolio_exited: updated});
                        }}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input 
                          placeholder="Indication"
                          value={item.indication}
                          onChange={(e) => {
                            const updated = [...firmForm.portfolio_exited];
                            updated[idx].indication = e.target.value;
                            setFirmForm({...firmForm, portfolio_exited: updated});
                          }}
                        />
                        <Input 
                          placeholder="Modality"
                          value={item.modality}
                          onChange={(e) => {
                            const updated = [...firmForm.portfolio_exited];
                            updated[idx].modality = e.target.value;
                            setFirmForm({...firmForm, portfolio_exited: updated});
                          }}
                        />
                        <Input 
                          placeholder="Outcome (IPO, Acquired by...)"
                          value={item.outcome}
                          onChange={(e) => {
                            const updated = [...firmForm.portfolio_exited];
                            updated[idx].outcome = e.target.value;
                            setFirmForm({...firmForm, portfolio_exited: updated});
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
            
            {/* Trusted Network Tab */}
            <TabsContent value="network" data-testid="network-tab-content">
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Add people in this investor's trusted network (co-investors, portfolio CEOs, board members, etc.) to help users find intro paths.
                </p>
                
                {/* Add New Trusted Contact Form */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-3">Add Trusted Contact</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Name *</Label>
                      <Input 
                        value={trustedContactForm.name} 
                        onChange={(e) => setTrustedContactForm({...trustedContactForm, name: e.target.value})} 
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label>Company</Label>
                      <Input 
                        value={trustedContactForm.company} 
                        onChange={(e) => setTrustedContactForm({...trustedContactForm, company: e.target.value})} 
                        placeholder="Syncona Partners"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label>Role</Label>
                      <Input 
                        value={trustedContactForm.role} 
                        onChange={(e) => setTrustedContactForm({...trustedContactForm, role: e.target.value})} 
                        placeholder="Partner"
                      />
                    </div>
                    <div>
                      <Label>Relationship Type</Label>
                      <Select 
                        value={trustedContactForm.relationship_type} 
                        onValueChange={(v) => setTrustedContactForm({...trustedContactForm, relationship_type: v})}
                      >
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="co_investor">Co-Investor</SelectItem>
                          <SelectItem value="portfolio_ceo">Portfolio CEO</SelectItem>
                          <SelectItem value="board_member">Board Member</SelectItem>
                          <SelectItem value="event_speaker">Event Speaker</SelectItem>
                          <SelectItem value="press_mention">Press Mention</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Comment * (how they're connected)</Label>
                    <Input 
                      value={trustedContactForm.comment} 
                      onChange={(e) => setTrustedContactForm({...trustedContactForm, comment: e.target.value})} 
                      placeholder="Co-invested in 3 deals together"
                    />
                  </div>
                  <div className="mt-3">
                    <Label>LinkedIn URL</Label>
                    <Input 
                      value={trustedContactForm.linkedin_url} 
                      onChange={(e) => setTrustedContactForm({...trustedContactForm, linkedin_url: e.target.value})} 
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <Button 
                    onClick={() => {
                      if (!trustedContactForm.name || !trustedContactForm.comment) {
                        toast.error("Name and comment are required");
                        return;
                      }
                      const newContact = {
                        contact_id: `tc_${Date.now()}`,
                        ...trustedContactForm
                      };
                      setFirmForm({
                        ...firmForm, 
                        trusted_network: [...(firmForm.trusted_network || []), newContact]
                      });
                      setTrustedContactForm({ name: "", comment: "", company: "", role: "", linkedin_url: "", relationship_type: "" });
                      toast.success("Contact added");
                    }}
                    className="mt-3 w-full"
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Contact
                  </Button>
                </div>
                
                {/* Existing Trusted Contacts List */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">
                    Trusted Network ({(firmForm.trusted_network || []).length} contacts)
                  </h4>
                  {(firmForm.trusted_network || []).length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No trusted contacts added yet</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {(firmForm.trusted_network || []).map((contact, idx) => (
                        <div key={contact.contact_id || idx} className="p-3 bg-white border border-slate-200 rounded-lg flex items-start justify-between">
                          <div>
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              {contact.name}
                              {contact.linkedin_url && (
                                <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                                  <Globe className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                            {(contact.role || contact.company) && (
                              <div className="text-sm text-slate-600">
                                {contact.role}{contact.role && contact.company ? ', ' : ''}{contact.company}
                              </div>
                            )}
                            <div className="text-xs text-teal-600 mt-1">{contact.comment}</div>
                            {contact.relationship_type && (
                              <Badge variant="outline" className="text-xs mt-1">{contact.relationship_type.replace(/_/g, ' ')}</Badge>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setFirmForm({
                                ...firmForm,
                                trusted_network: (firmForm.trusted_network || []).filter((_, i) => i !== idx)
                              });
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setFirmDialog({ open: false, firm: null })} data-testid="cancel-firm-button">Cancel</Button>
            <Button onClick={handleSaveFirm} disabled={saving} data-testid="save-firm-button">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialog.open} onOpenChange={(open) => setContactDialog({ open, contact: contactDialog.contact })}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{contactDialog.contact ? "Edit Contact" : "Add Contact"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={contactForm.first_name} onChange={(e) => setContactForm({...contactForm, first_name: e.target.value})} />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={contactForm.last_name} onChange={(e) => setContactForm({...contactForm, last_name: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Firm</Label>
              <Select value={contactForm.firm_id || "none"} onValueChange={(v) => setContactForm({...contactForm, firm_id: v === "none" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select firm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Independent</SelectItem>
                  {firms.map(f => (
                    <SelectItem key={f.firm_id} value={f.firm_id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Input value={contactForm.role} onChange={(e) => setContactForm({...contactForm, role: e.target.value})} placeholder="Partner" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input value={contactForm.linkedin_url} onChange={(e) => setContactForm({...contactForm, linkedin_url: e.target.value})} />
            </div>
            <div>
              <Label>Board Seats (comma-separated)</Label>
              <Input value={contactForm.board_seats} onChange={(e) => setContactForm({...contactForm, board_seats: e.target.value})} />
            </div>
            <div>
              <Label>Intel (interests, notes)</Label>
              <Textarea value={contactForm.intel} onChange={(e) => setContactForm({...contactForm, intel: e.target.value})} rows={2} />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={contactForm.is_primary_contact} onChange={(e) => setContactForm({...contactForm, is_primary_contact: e.target.checked})} />
              Primary Contact
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialog({ open: false, contact: null })}>Cancel</Button>
            <Button onClick={handleSaveContact} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.type === "firm" ? "Firm" : "Contact"}</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Are you sure you want to delete {deleteDialog.type === "firm" ? deleteDialog.item?.name : `${deleteDialog.item?.first_name} ${deleteDialog.item?.last_name}`}?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
